/**
 * Teaching pearls for ChartMind's Pearls step, from the chartmind-pearls prompt (originally
 * ChartMind V2's, copied from MinusOne's DecisionPoint Quick Draft pearls).
 *
 * ⚠️ THE PROMPT DOC MAY NOT EXIST YET on a given environment; it is seeded by
 * scripts/seedChartMindPrompts.js. Until it is, the call fails with a missing-prompt error and
 * ChartMind hides the step rather than showing one that can only ever look broken.
 */
import { invokeLLMPrompt } from "services/llmService";

export const PEARLS_PROMPT_ID = "chartmind-pearls";

/** The error callLLMAPI throws when a prompt has not been set up on this environment. */
export const isMissingPromptError = (err) => /No prompt content is available/i.test(err?.message || "");

export function parsePearls(content) {
  if (!content) return [];
  const trimmed = String(content).trim();
  if (!trimmed.includes("{") && !trimmed.includes("[")) return [];

  let jsonStr = trimmed;
  const fence = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/);
  if (fence) jsonStr = fence[1];

  let parsed;
  try {
    parsed = JSON.parse(jsonStr.trim());
  } catch {
    return [];
  }

  const list = Array.isArray(parsed) ? parsed : parsed?.pearls;
  if (!Array.isArray(list)) return [];

  return list
    .map((item, index) => {
      if (typeof item === "string") {
        return { id: `pearl-${index}`, title: "", body: item.trim() };
      }
      if (!item || typeof item !== "object") return null;
      const body = String(item.body || item.pearl || item.text || "").trim();
      if (!body) return null;
      return {
        id: String(item.id || `pearl-${index}`),
        title: String(item.title || item.topic || "").trim(),
        body,
      };
    })
    .filter(Boolean);
}

/**
 * @returns {Promise<Array<{id: string, title: string, body: string}>>}
 */
export async function generatePearls({
  subject,
  organizationId = null,
  promptId = PEARLS_PROMPT_ID,
  fallbackPromptId,
  focus,
  patientContext = null,
}) {
  const text = String(subject || "").trim();
  if (!text) return [];

  const result = await invokeLLMPrompt({
    promptId,
    fallbackPromptId,
    focus,
    userMessage: `CLINICAL SUBJECT:\n\n${text}`,
    organizationId: organizationId || undefined,
    patientContext,
  });

  return parsePearls(result?.output);
}
